import Feature from "ol/Feature";
import { State } from ".";

import olFeature from "ol/Feature";
import olTile from "ol/Tile";
import olTileState from "ol/TileState";
import olVectorTile from "ol/VectorTile";
import olFeatureFormat from "ol/format/Feature";
import MVT from "ol/format/MVT";
import { ProjectionLike as olProjectionLike } from "ol/proj";
import { TileSourceEvent } from "ol/source/Tile";
import olVectorTileSource from "ol/source/VectorTile";
import olTileGrid from "ol/tilegrid/TileGrid";
import { Geometry } from "ol/geom";

/**
 * Event fired during loading state change
 * @param {import(".").State} state The new loading state
 */
export type StateChangeEvent = (state: State) => void;

export interface VectorTileProps {
  url: string;
  projection: olProjectionLike;
  tileGrid: olTileGrid;
  format: olFeatureFormat;
  onStateChange: StateChangeEvent;
  authorizationToken: string | null;
}

/**
 * Service to create an OL VectorTile source for use in VectorTile layers.
 * The service handles the loading of the data, assigning it an ID if required
 * and maintaining it's laoding state across all requested tiles.
 * @param  {string} url The URL template. Must include {x}, {y} or {-y}, and {z} placeholders.
 * A {?-?} template pattern, for example subdomain{a-f}.domain.com, may also be used.
 * @param {import("ol/proj").ProjectionLike} projection The projection to use in this source
 * @param {import("ol/tilegrid/TileGrid").default} tileGrid The Tile Grid to use to slice the map into tile
 * @param {import("ol/format/Feature").default} format The feature formatter to use to read the data from the provided URL template
 * @param {StateChangeEvent}  onStateChange Callback function fired when the source's loading state changes.
 * @returns {import("ol/source/VectorTile").default} a VectorTile source
 */
const VectorTile = ({
  url,
  projection,
  tileGrid,
  format,
  onStateChange,
  authorizationToken,
}: VectorTileProps) => {
  let state = State.Unloaded;

  const isMVT = format instanceof MVT;

  const source = new olVectorTileSource({
    url,
    projection,
    tileGrid,
    format,

    ...(isMVT ?
      {
        tileLoadFunction: function (tile, url) {
          const vectorTile = tile as olVectorTile;
          fetch(url)
            .then((response) => {
              if (!response.ok) {
                vectorTile.setFeatures([]);
                vectorTile.setState(olTileState.LOADED);
                return null;
              }
              return response.arrayBuffer();
            })
            .then((buffer) => {
              if (!buffer) return;

              const features = format.readFeatures(buffer, {
                extent: vectorTile.extent,
                featureProjection: "EPSG:3857",
              }) as Feature<Geometry>[];

              vectorTile.setFeatures(features);
              vectorTile.setState(olTileState.LOADED);
            })
            .catch(() => {
              vectorTile.setFeatures([]);
              vectorTile.setState(olTileState.LOADED);
            });
        },
      }
    : {
        tileLoadFunction: async function (tile: olTile, url) {
          let headers: HeadersInit = {};

          if (authorizationToken) {
            headers = new Headers({
              Authorization: authorizationToken,
            });
          }

          fetch(url, { headers })
            .then(async (response) => {
              if (!response.ok) {
                return Promise.reject(response.status);
              }
              return response.json();
            })
            .then((data) => {
              const features = format.readFeatures(data, {
                dataProjection: format.readProjection(data),
                featureProjection: projection,
              }) as olFeature[];

              features.forEach((f) => {
                if (!f.getId()) {
                  const properties = f.getProperties();
                  const id =
                    properties["id"] ?? properties["Id"] ?? properties["ID"];
                  if (id !== undefined) {
                    f.setId(id);
                  }
                }
              });

              (tile as olVectorTile).setFeatures(features);
              (tile as olVectorTile).setState(olTileState.LOADED);
            })
            .catch(() => {
              tile.setState(olTileState.ERROR);
            });
        },
      }),
  });

  if (!isMVT) {
    let tileLoadCounter = 0;
    const handleLoadStart = () => {
      if (state !== State.Loading) {
        state = State.Loading;
        onStateChange(State.Loading);
      }
      tileLoadCounter++;
    };
    const handleLoadEnd = (e: TileSourceEvent) => {
      tileLoadCounter--;
      if (e.type === "tileloaderror" && state !== State.Error) {
        state = State.Error;
        onStateChange(State.Error);
      } else if (tileLoadCounter === 0 && state === State.Loading) {
        state = State.Ready;
        onStateChange(State.Ready);
      }
    };

    source.on("tileloadstart", handleLoadStart);
    source.on("tileloadend", handleLoadEnd);
    source.on("tileloaderror", handleLoadEnd);
  }

  return source;
};

export default VectorTile;
